import { route, tag, type PageTour } from '../pageTour';
import type { TourLocation } from '../tourDefinitions';

/*
 * Search & Track. Two modes on one URL (?mode=track), three panes that fold, and a filter column
 * whose contents depend on how much the site's search can count — so most steps drop out by
 * themselves when their control isn't on screen, and the mode-specific ones carry a `when`.
 *
 * Written for someone who has never used the product: what each thing is FOR, in plain words.
 */

const isTrack = ({ params }: TourLocation) => params.get('mode') === 'track';
const isSearch = (at: TourLocation) => !isTrack(at);
/** The filter column is folded to a thin strip (remembered by the page in localStorage). */
const filtersFolded = () => {
  try { return localStorage.getItem('trinetra.search.panel') === '0'; } catch { return false; }
};

export const searchTour: PageTour = {
  id: 'page-search',
  version: 2,
  title: 'Search & Track',
  summary: 'Search detections by person, face or vehicle and track them across cameras.',
  match: route('/search'),
  steps: [
    // ── The top bar ──
    {
      target: tag('search-query'),
      title: 'Ask in plain words',
      description:
        'Type what you are looking for the way you would say it, for example "man in a red shirt today" or a car number plate. After a short pause the search runs by itself; press Enter to run it straight away. Above the results it shows how your words were understood. The × clears what you typed.',
      side: 'bottom',
    },
    {
      target: tag('search-mode'),
      title: 'Search or Track',
      description:
        'You are in Search: every match is shown as its own card, with everything the camera noticed about it. Switch to Track to see the same matches as a trail — where someone went, camera by camera, in time order. Your filters stay when you switch. Press ? again after switching to learn Track.',
      side: 'bottom',
      when: isSearch,
    },
    {
      target: tag('search-mode'),
      title: 'Search or Track',
      description:
        'You are in Track: the matches are joined into a trail of where someone went, camera by camera, in time order, and drawn on a map. Switch to Search to see every match as its own card instead. Your filters stay when you switch. Press ? again after switching to learn Search.',
      side: 'bottom',
      when: isTrack,
    },
    {
      target: tag('search-panes'),
      title: 'Show or hide the map and results',
      description:
        'Turn the tracking map and the results list on or off to give the other more room. One of them always stays open.',
      side: 'bottom',
      when: isTrack,
    },
    {
      target: tag('search-filters-toggle'),
      title: 'Show or hide the filters',
      description:
        'Opens or folds the filter column on the left. When it is folded and filters are switched on, a number here shows how many.',
      side: 'bottom',
    },
    {
      target: tag('search-refresh'),
      title: 'Search again',
      description: 'Runs the same search again to pick up anything new the cameras have seen since.',
      side: 'bottom',
    },

    // ── The filter column ──
    {
      target: tag('search-filters-column'),
      title: 'The filters are folded',
      description:
        'The filter column is folded into this thin strip to give the results more room. Click it to open the filters again.',
      side: 'right',
      when: filtersFolded,
    },
    {
      target: tag('search-filters'),
      title: 'Filters',
      description:
        'Everything here narrows down the results. "Clear all" at the top removes every filter at once, and the « button folds this column away.',
      side: 'right',
    },
    {
      target: tag('search-subject'),
      title: 'What you are looking for',
      description: 'Choose Person, Vehicle or All. The other filters change to suit your choice.',
      side: 'right',
    },
    {
      target: tag('search-when'),
      title: 'When',
      description:
        'Choose how far back to look in one click: All time, Today, Yesterday, the last 7 or 30 days, this month or last month. "Custom range…" lets you pick exact start and end dates and times.',
      side: 'right',
    },
    {
      target: tag('search-spelling'),
      title: 'Spelling — how exact to be',
      description:
        'This only matters for words you type, such as a name or a number plate. Exact finds the word exactly as typed. Close also allows one wrong letter (rajn finds rajan). Loose also finds words that sound alike or were misread by the camera (jai finds jay). Loose finds the most.',
      side: 'right',
    },
    {
      target: tag('search-refine-notice'),
      title: 'Why some choices are missing',
      description:
        'There are so many results that the page cannot count them by type, camera and appearance. Your results are still correct. Type a word or pick a shorter time and the extra choices come back.',
      side: 'right',
    },
    {
      target: tag('search-refine-types'),
      title: 'Event types',
      description:
        'What kind of detection each result is, such as face recognition, person, vehicle or number plate, with how many of each. Untick a type to remove it from the results; tick it again to bring it back.',
      side: 'right',
    },
    {
      target: tag('search-refine-cameras'),
      title: 'Seen on — cameras',
      description: 'Which cameras saw your results, with counts. Tick cameras to see only their results.',
      side: 'right',
    },
    {
      target: tag('search-refine-people'),
      title: 'People — appearance',
      description:
        'Narrow people down by how they look: top and bottom clothing colour, gender, age group and things they carry. The numbers show how many results have each.',
      side: 'right',
    },
    {
      target: tag('search-refine-vehicles'),
      title: 'Vehicles — appearance',
      description: 'Narrow vehicles down by type, colour and the direction they were driving.',
      side: 'right',
    },
    {
      target: tag('search-refine-watchlist'),
      title: 'Watch list',
      description: 'Show only people or vehicles that are on a watch list, or only those that are not.',
      side: 'right',
    },
    {
      target: tag('search-legacy-where'),
      title: 'Where',
      description: 'Limit the search to certain camera groups or single cameras.',
      side: 'right',
    },
    {
      target: tag('search-legacy-types'),
      title: 'Event types',
      description: 'Choose which kinds of detection to search, such as faces, people or vehicles.',
      side: 'right',
    },
    {
      target: tag('search-legacy-appearance'),
      title: 'Appearance',
      description: 'Narrow results by how someone or something looked, such as clothing or vehicle colour.',
      side: 'right',
    },
    {
      target: tag('search-legacy-identity'),
      title: 'Name, plate and text',
      description:
        'Type a person\'s name, a number plate, or words seen on a sign, and choose whether to include people who are enrolled (known) or not.',
      side: 'right',
    },

    // ── Track: the map ──
    {
      target: tag('search-track-map'),
      title: 'The tracking map',
      description:
        'Shows the route on a map. The numbered pins are the stops in time order, so you can follow where someone went. At the top it says how many stops and how many different people or vehicles were found. The square button makes the map full screen (Esc to leave), and Hide folds it away.',
      side: 'right',
      when: isTrack,
    },
    {
      target: tag('search-track-following'),
      title: 'Whose route is drawn',
      description:
        'When the results include more than one known person or vehicle, their routes are never mixed together. Pick here whose route to draw. This only changes the map, not the list.',
      side: 'bottom',
      when: isTrack,
    },
    {
      target: tag('search-track-canvas'),
      title: 'Floor plan or street map',
      description:
        'GIS draws the route on the building floor plan. Map draws it on a street map. If one of them has not been set up for this site, its button is greyed out.',
      side: 'bottom',
      when: isTrack,
    },
    {
      target: tag('map-basemap'),
      title: 'Map style',
      description:
        'Change how the map looks: Map, Streets, Satellite or Terrain. Satellite and Terrain need an internet connection.',
      side: 'top',
      when: isTrack,
    },
    {
      target: tag('search-track-resize'),
      title: 'Make the map or the list wider',
      description: 'Drag this line left or right to share the space between the map and the results. Double-click it to reset.',
      side: 'left',
      when: isTrack,
    },

    // ── Results ──
    {
      target: tag('search-chips'),
      title: 'Filters you are using',
      description:
        'A summary of every filter that is on, so you always know why you are seeing these results, even with the filter column folded. Click the × on one to remove it.',
      side: 'bottom',
    },
    {
      target: tag('search-status'),
      title: 'How much was found',
      description:
        '"Detections" is how many times the cameras saw something that matches, over the whole time you chose — not just this page. "Distinct people & vehicles" is how many different ones that adds up to. "Across N cameras" is how many cameras you are allowed to search.',
      side: 'bottom',
    },
    {
      target: tag('search-view-controls'),
      title: 'Search speed',
      description: 'The number of milliseconds (ms) shows how long the search took.',
      side: 'bottom',
      when: isSearch,
    },
    {
      target: tag('search-per-row'),
      title: 'Cards per row',
      description:
        'AUTO sizes the cards to fit the screen. 1, 2, 3 or 4 sets how many cards sit side by side. Your choice is remembered.',
      side: 'bottom',
      when: isTrack,
    },
    {
      target: tag('search-sort'),
      title: 'Newest or oldest first',
      description:
        'Flips the order of the stops on this page: newest first, or oldest first to read the route from the start.',
      side: 'bottom',
      when: isTrack,
    },
    {
      target: tag('search-results'),
      title: 'Your results',
      description:
        'Every match appears here as a card. Click the picture or Details to open the full record. Playback plays a short video clip from around that moment.',
      side: 'left',
      when: isSearch,
    },
    {
      target: tag('search-results'),
      title: 'Your trail',
      description:
        'The matches appear here as a trail of stops, in time order, so you can read where someone went. The map shows the same stops as numbered pins. Click a stop\'s picture to open its full record, or its play button to watch the video.',
      side: 'left',
      when: isTrack,
    },
    {
      target: tag('event-card'),
      title: 'One result',
      description:
        'The picture the camera took, which camera saw it, when, and what was noticed (such as clothing colour or a number plate). Use Playback to watch the video, or Details for everything about it.',
      side: 'right',
      when: isSearch,
    },
    {
      target: tag('search-track-summary'),
      title: 'The trail in one line',
      description:
        'How many stops, at how many cameras, for how many people or vehicles, and the time from the first stop to the last. If there are more results than fit on one page, it says the trail is made from this page only.',
      side: 'bottom',
      when: isTrack,
    },
    {
      target: tag('search-track-day'),
      title: 'Grouped by day',
      description: 'Stops are grouped under the day they happened, with how many stops there were that day.',
      side: 'bottom',
      when: isTrack,
    },
    {
      target: tag('search-track-stop'),
      title: 'A stop',
      description:
        'One stop is one visit to one camera: if the same person was seen several times at the same camera within a few minutes, those sightings are joined into one stop. The arrows between stops show the order, such as "from Camera Lockers · 35s earlier", or "also here" when they stayed at the same camera. A green edge marks the earliest stop on the page, orange the latest. Click the picture to open the full record.',
      side: 'right',
      when: isTrack,
    },
    {
      target: tag('search-track-play'),
      title: 'Play the video',
      description: 'Plays the recorded video from the moment of this stop, right on this page.',
      side: 'left',
      when: isTrack,
    },
    {
      target: tag('search-pager'),
      title: 'More results',
      description:
        'Results come 100 at a time. Use Previous and Next to move between pages; the middle shows which ones you are looking at.',
      side: 'top',
    },
  ],
};
